<template>
  <div v-if="isStacked" class="slot-day-heading" :class="modifierClasses">
    <sp-text class="slot-day-heading__title" :type="titleTypeScale">{{ shortDate }}</sp-text>
    <sp-footnote class="slot-day-heading__subtitle">{{ weekday }}</sp-footnote>
  </div>

  <sp-text v-else class="slot-day-heading slot-day-heading__line" tag="h4" type="custom">{{ longDate }}</sp-text>
</template>

<script setup>
import { computed } from "vue";
import { formatOrdinalDate } from "#/shared/lib/helpers/localized-date";
import { formatShortDate, formatWeekday, toEntryDate } from "#ce/components/SpFinder/view-models/slot-format";

const props = defineProps({
  /**
   * The day: a `"YYYY-MM-DD"` string or a Date.
   *
   * @type {String|Date}
   */
  date: {
    type: [String, Date],
    required: true,
  },
  /**
   * "stacked" puts the short date over the weekday; "line" states the whole date on one line.
   *
   * @type {String}
   */
  variant: {
    type: String,
    default: "stacked",
    validator: (value) => ["stacked", "line"].includes(value),
  },
  /**
   * Shrinks the stacked heading and abbreviates its weekday.
   *
   * @type {Boolean}
   */
  compact: {
    type: Boolean,
    default: false,
  },
  /**
   * When the date states its year: "never", "next_year" or "always".
   *
   * @type {String}
   */
  showYear: {
    type: String,
    default: "never",
    validator: (value) => ["never", "next_year", "always"].includes(value),
  },
});

const day = computed(() => toEntryDate(props.date));
const isStacked = computed(() => props.variant === "stacked");
const shortDate = computed(() => formatShortDate(day.value, props.showYear));
const longDate = computed(() => formatOrdinalDate(day.value, props.showYear));
const weekday = computed(() => formatWeekday(day.value, props.compact));
const titleTypeScale = computed(() => (props.compact ? "body-bold" : "h4"));
const modifierClasses = computed(() => ({ "--compact": props.compact }));
</script>

<style lang="scss" scoped>
.slot-day-heading__title {
  --sp-sys-typescale-h4-short: var(
    --sp-comp-slot-day-heading-title-font-short,
    var(--sp-sys-typescale-h4-font-weight) var(--sp-sys-typescale-h4-font-size) / var(--sp-sys-typescale-h4-line-height)
      var(--sp-sys-typescale-h4-font-family)
  );
}

.slot-day-heading.--compact .slot-day-heading__title {
  font: var(--sp-comp-slot-day-heading-title-font-short-compact, var(--sp-sys-typescale-body-bold-short));
}

.slot-day-heading__subtitle {
  --sp-sys-typescale-footnote-short: var(
    --sp-comp-slot-day-heading-subtitle-font-short,
    var(--sp-sys-typescale-footnote-font-weight) var(--sp-sys-typescale-footnote-font-size) /
      var(--sp-sys-typescale-footnote-line-height) var(--sp-sys-typescale-footnote-font-family)
  );
}

.slot-day-heading__line {
  --sp-comp-text-custom-font-short: var(--sp-comp-slot-day-heading-font-short, var(--sp-sys-typescale-h4-short));
  --sp-comp-text-color: var(--sp-comp-slot-day-heading-color, var(--sp-sys-color-on-surface));

  margin-block: var(--sp-comp-slot-day-heading-margin-block-start, 0)
    var(--sp-comp-slot-day-heading-margin-block-end, 0);
}
</style>
